import { AreaStack, Line } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import {
    DataSourceGlyphCircle,
    TooltipDate,
    TooltipEstimateNote,
    TooltipMetric,
    TooltipMetricLabel,
    TooltipMetricLabelWrapper,
    TooltipName,
} from './tooltip-components';
import { useCallback, useMemo, useState } from 'react';
import { Chart } from 'components/DataVis/TimeSeriesChart';
import DataSourceAreaStacks from '../../DataSourceAreaStacks';
import Drawer from 'components/Drawer';
import DriversOfGrowthDrawer from '../MultiPredictionTimeSeriesChart/components/DriversOfGrowthDrawer';
import DriversOfGrowthTooltip from '../MultiPredictionTimeSeriesChart/components/DriversOfGrowthTooltip';
import ErrorBoundary from 'containers/ErrorBoundary';
import { Group } from '@visx/group';
import { ParentSize } from '@visx/responsive';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import Stack from 'components/Stack';
import TickLabel from '../MultiPredictionTimeSeriesChart/components/TickLabel';
import TimeSeriesKey from '../../TimeSeriesKey';
import TooltipCard from '../MultiPredictionTimeSeriesChart/components/TooltipCard';
import { bisector } from 'd3-array';
import dayjs from 'dayjs';
import { formatVolume } from 'utils/helpers';
import { getStackedChartFns } from '../../utils';
import { getXAxisTickFns } from './utils';
import { localPoint } from '@visx/event';
import styled from 'styled-components';
import { useTooltip } from '@visx/tooltip';
import { yAxisTickFormat } from '../MultiPredictionTimeSeriesChart/utils';

const X_AXIS_HEIGHT = 50;
const Y_AXIS_WIDTH = 70;
const STROKE_WIDTH = 2;
const SOURCE_PRIORITY = ['tiktok', 'instagram', 'google'];
const SOURCE_LABEL_BY_ID = {
    google: 'Google',
    instagram: 'Instagram',
    tiktok: 'TikTok',
};
const SOURCE_METRIC_LABEL_BY_ID = {
    google: 'searches',
    instagram: 'views',
    tiktok: 'views',
};
const SOURCE_CURRENT_VOLUME_LABEL_BY_ID = {
    google: 'Searches',
    instagram: 'Views',
    tiktok: 'Views',
};
const POP_INDEX_SERIES_TO_DRIVERS_SOURCE = {
    google: 'pop_index_google',
    instagram: 'pop_index_instagram',
    tiktok: 'pop_index_tiktok',
};

const formatAndParseTime = (v) => {
    const s = dayjs(v, 'YYYY-MM-DD').format('MMM YYYY');
    return s;
};

const Placeholder = styled.div.attrs(({ shimmerWidth = '10000px' }) => ({
    shimmerWidth,
}))`
    grid-area: chart;
    min-height: ${({ height }) => `${height}px`};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: ${({ theme }) => theme.borderRadius_sm};
    background: ${({ $showBackground, theme }) =>
        $showBackground ? theme.gray_5 : 'transparent'};
    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : null)}
`;

const TikTokTimeSeriesWrapper = styled.div`
    display: block;
`;

function PopularityTimeSeriesTooltipSVG({
    tooltipData,
    keys,
    stackData,
    predictionKeys,
    predictionStackData,
    y0,
    y1,
    x,
    yRange0,
    yRange1,
}) {
    const tooltipDataIsPrediction = !!tooltipData.overview;
    const areaStackKeys = tooltipDataIsPrediction ? predictionKeys : keys;
    const areaStackData = tooltipDataIsPrediction
        ? predictionStackData
        : stackData;
    return (
        <Group>
            <Line
                from={{ x: x({ data: tooltipData }), y: yRange0 }}
                to={{ x: x({ data: tooltipData }), y: yRange1 }}
                stroke="#3A3A3A"
                strokeWidth={1}
                strokeDasharray="2 2"
            />
            <AreaStack
                top={0}
                left={0}
                keys={areaStackKeys}
                data={areaStackData}
                x={x}
                y0={y0}
                y1={y1}
            >
                {({ stacks }) => {
                    return stacks.map((stack) => {
                        const stackPoint = stack.find(
                            (d) => d.data.date === tooltipData.date,
                        );
                        if (!stackPoint) return null;
                        return (
                            <DataSourceGlyphCircle
                                key={`circle-${stack.key}`}
                                left={x(stackPoint)}
                                top={y1(stackPoint)}
                                size={130}
                                $dataSource={stack.key}
                            />
                        );
                    });
                }}
            </AreaStack>
        </Group>
    );
}

function MultiTimeSeriesChart({
    data,
    width,
    height,
    name,
    country,
    category,
    categoryDepth,
    brand,
    driversEnabled = false,
}) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPointData, setSelectedPointData] = useState(null);
    const [selectedDriversSourceId, setSelectedDriversSourceId] =
        useState(null);
    const [selectedDateKey, setSelectedDateKey] = useState(null);
    const { xAxisTickFormat, xAxisTickValues } = getXAxisTickFns(data);
    const hasDriversOfGrowthContext =
        driversEnabled && Boolean(country && (category || brand));
    const xRange0 = Y_AXIS_WIDTH;
    const xRange1 = width;
    const yRange0 = height - X_AXIS_HEIGHT;
    const yRange1 = X_AXIS_HEIGHT;
    const {
        xMin,
        xMax,
        x,
        y0,
        y1,
        yScale,
        xScale,
        stackData,
        keys,
        predictionStackData,
        predictionKeys,
        accessors,
        formattedData,
    } = getStackedChartFns({
        series: data,
        xRange0,
        xRange1,
        yRange0,
        yRange1,
    });

    const {
        showTooltip,
        hideTooltip,
        tooltipData,
        tooltipLeft,
        tooltipTop,
        tooltipOpen,
    } = useTooltip({
        tooltipOpen: false,
        tooltipLeft: 0,
        tooltipTop: 0,
        tooltipData: null,
    });

    const sourceSeriesById = useMemo(
        () =>
            formattedData.reduce((acc, series) => {
                if (series.id !== 'overview') {
                    acc[series.id] = series;
                }
                return acc;
            }, {}),
        [formattedData],
    );

    const sourceRowsByDate = useMemo(
        () =>
            Object.entries(sourceSeriesById).reduce(
                (acc, [sourceId, series]) => {
                    acc[sourceId] = series.historical.reduce(
                        (seriesMap, row) => {
                            const dateKey = dayjs(
                                accessors.xAccessor(row),
                            ).format('YYYY-MM-DD');
                            seriesMap[dateKey] = row;
                            return seriesMap;
                        },
                        {},
                    );
                    return acc;
                },
                {},
            ),
        [sourceSeriesById, accessors],
    );

    const sourceMetricsByDate = useMemo(
        () =>
            Object.entries(sourceSeriesById).reduce(
                (acc, [sourceId, series]) => {
                    let previousValue = null;
                    acc[sourceId] = {};
                    series.historical.forEach((row) => {
                        const dateKey = dayjs(accessors.xAccessor(row)).format(
                            'YYYY-MM-DD',
                        );
                        const value = accessors.yAccessor(row);
                        const explicitIncrease = accessors.momChangeAccessor
                            ? accessors.momChangeAccessor(row)
                            : accessors.wowChangeAccessor
                              ? accessors.wowChangeAccessor(row)
                              : null;
                        const computedIncrease =
                            value != null && previousValue != null
                                ? value - previousValue
                                : null;
                        acc[sourceId][dateKey] = {
                            datum: row,
                            value,
                            increase: explicitIncrease ?? computedIncrease,
                        };
                        if (value != null) {
                            previousValue = value;
                        }
                    });
                    return acc;
                },
                {},
            ),
        [sourceSeriesById, accessors],
    );

    const sourceIds = useMemo(
        () => Object.keys(sourceSeriesById),
        [sourceSeriesById],
    );

    const availableSourceIds = useMemo(
        () =>
            ['google', 'tiktok', 'instagram'].filter(
                (sourceId) => sourceSeriesById[sourceId],
            ),
        [sourceSeriesById],
    );

    const getNearestStackDatum = useCallback(
        (pointX) => {
            const bisectDate = bisector((d) => new Date(d.date)).right;
            const xDate = xScale.invert(pointX);
            const index = bisectDate(stackData, xDate);
            const predictionIndex = bisectDate(predictionStackData, xDate);
            const closestIndex =
                index >= stackData.length ? index - 1 : index < 0 ? 0 : index;
            const closestPredictionIndex =
                predictionIndex >= predictionStackData.length
                    ? predictionIndex - 1
                    : predictionIndex < 0
                      ? 0
                      : predictionIndex;
            return closestPredictionIndex > 0
                ? predictionStackData[closestPredictionIndex]
                : stackData[closestIndex];
        },
        [predictionStackData, stackData, xScale],
    );

    const getDominantDriversPointByDate = useCallback(
        (dateValue) => {
            const dateKey = dayjs(dateValue).format('YYYY-MM-DD');
            if (!dateKey) return null;

            const candidates = sourceIds
                .map((sourceId) => {
                    const metric = sourceMetricsByDate[sourceId]?.[dateKey];
                    if (!metric?.datum) return null;
                    const changeValue = metric.increase;
                    const value = metric.value ?? 0;
                    return {
                        sourceId,
                        dataSource:
                            POP_INDEX_SERIES_TO_DRIVERS_SOURCE[sourceId],
                        datum: metric.datum,
                        changeValue,
                        absChange:
                            changeValue == null ? -1 : Math.abs(changeValue),
                        value,
                        priority: SOURCE_PRIORITY.indexOf(sourceId),
                    };
                })
                .filter(Boolean);

            if (candidates.length === 0) return null;

            candidates.sort((a, b) => {
                const aHasChange = a.absChange >= 0;
                const bHasChange = b.absChange >= 0;
                if (aHasChange !== bHasChange) return aHasChange ? -1 : 1;
                if (a.absChange !== b.absChange)
                    return b.absChange - a.absChange;
                if (a.value !== b.value) return b.value - a.value;
                return (
                    (a.priority === -1 ? 999 : a.priority) -
                    (b.priority === -1 ? 999 : b.priority)
                );
            });

            return candidates[0];
        },
        [sourceIds, sourceMetricsByDate, accessors],
    );

    const tooltipDateKey = useMemo(
        () =>
            tooltipData?.date
                ? dayjs(tooltipData.date).format('YYYY-MM-DD')
                : null,
        [tooltipData],
    );

    const tooltipDominantSourcePoint = useMemo(
        () =>
            tooltipData && !tooltipData.overview
                ? getDominantDriversPointByDate(tooltipData.date)
                : null,
        [tooltipData, getDominantDriversPointByDate],
    );

    const tooltipDominantSourceId =
        tooltipDominantSourcePoint?.sourceId ||
        availableSourceIds.find((id) => id !== 'google') ||
        availableSourceIds[0] ||
        null;

    const tooltipDominantSource = tooltipDominantSourceId
        ? POP_INDEX_SERIES_TO_DRIVERS_SOURCE[tooltipDominantSourceId]
        : null;

    const tooltipDominantDatum = useMemo(() => {
        if (!tooltipDateKey || !tooltipDominantSourceId) return null;
        return (
            sourceRowsByDate[tooltipDominantSourceId]?.[tooltipDateKey] || null
        );
    }, [tooltipDateKey, tooltipDominantSourceId, sourceRowsByDate]);

    const tooltipTotalValue = useMemo(() => {
        if (!(tooltipData && !tooltipData.overview)) return null;
        let hasAny = false;
        const total = availableSourceIds.reduce((acc, sourceId) => {
            const value = tooltipData[sourceId];
            if (value != null) {
                hasAny = true;
                return acc + value;
            }
            return acc;
        }, 0);
        return hasAny ? total : null;
    }, [tooltipData, availableSourceIds]);

    const tooltipTotalIncrease = useMemo(() => {
        if (!(tooltipData && !tooltipData.overview) || !tooltipDateKey)
            return null;
        let hasAny = false;
        const total = availableSourceIds.reduce((acc, sourceId) => {
            const increase =
                sourceMetricsByDate[sourceId]?.[tooltipDateKey]?.increase;
            if (increase != null) {
                hasAny = true;
                return acc + increase;
            }
            return acc;
        }, 0);
        return hasAny ? total : null;
    }, [tooltipData, availableSourceIds, sourceMetricsByDate, tooltipDateKey]);

    const tooltipDominantSourceImpact = useMemo(() => {
        if (
            !(tooltipData && !tooltipData.overview) ||
            !tooltipDateKey ||
            !tooltipDominantSourceId
        )
            return null;
        return {
            id: tooltipDominantSourceId,
            label: SOURCE_LABEL_BY_ID[tooltipDominantSourceId],
            increase:
                sourceMetricsByDate[tooltipDominantSourceId]?.[tooltipDateKey]
                    ?.increase ?? null,
            metricLabel:
                SOURCE_METRIC_LABEL_BY_ID[tooltipDominantSourceId] || 'views',
        };
    }, [
        tooltipData,
        tooltipDateKey,
        tooltipDominantSourceId,
        sourceMetricsByDate,
    ]);

    const tooltipDriversPointData = useMemo(() => {
        if (!(tooltipData && !tooltipData.overview) || !tooltipDateKey)
            return null;
        const hashtagsOrKeywordsCount = tooltipDominantDatum
            ? accessors.hashtagsCountAccessor
                ? accessors.hashtagsCountAccessor(tooltipDominantDatum)
                : accessors.keywordsCountAccessor
                  ? accessors.keywordsCountAccessor(tooltipDominantDatum)
                  : 0
            : 0;
        const postsCount =
            tooltipDominantDatum && accessors.postsCountAccessor
                ? accessors.postsCountAccessor(tooltipDominantDatum)
                : 0;
        return {
            date: tooltipDateKey,
            real_volume: tooltipTotalValue,
            non_cum_increase_MoM: tooltipTotalIncrease,
            hashtags_count: hashtagsOrKeywordsCount ?? 0,
            posts_count: postsCount ?? 0,
        };
    }, [
        tooltipData,
        tooltipDateKey,
        tooltipDominantDatum,
        tooltipTotalValue,
        tooltipTotalIncrease,
        accessors,
    ]);

    const predictionTooltipPointData = useMemo(() => {
        if (!(tooltipData && tooltipData.overview)) return null;
        const dateKey = tooltipData?.date
            ? dayjs(tooltipData.date).format('YYYY-MM-DD')
            : null;
        if (!dateKey) return null;
        return {
            date: dateKey,
            real_volume: tooltipData.overview,
            non_cum_increase_MoM: null,
            hashtags_count: 0,
            posts_count: 0,
        };
    }, [tooltipData]);

    const tooltipDriversAccessors = useMemo(
        () => ({
            xAccessor: (d) => d.date,
            yAccessor: (d) => d.real_volume,
            momChangeAccessor: (d) => d.non_cum_increase_MoM,
            hashtagsCountAccessor: (d) => d.hashtags_count,
            postsCountAccessor: (d) => d.posts_count,
        }),
        [],
    );

    const tooltipPredictionSource = useMemo(() => {
        const firstSourceId = availableSourceIds[0] || 'google';
        return (
            POP_INDEX_SERIES_TO_DRIVERS_SOURCE[firstSourceId] ||
            'pop_index_google'
        );
    }, [availableSourceIds]);

    const drawerActiveSourceId = selectedDriversSourceId;
    const drawerActiveSource = drawerActiveSourceId
        ? POP_INDEX_SERIES_TO_DRIVERS_SOURCE[drawerActiveSourceId]
        : null;
    const drawerActiveHistoricalData = drawerActiveSourceId
        ? sourceSeriesById[drawerActiveSourceId]?.historical || []
        : [];
    const drawerActiveSelectedPoint = selectedPointData;
    const drawerHeaderPrimaryMetric = useMemo(() => {
        if (!drawerActiveSourceId) return null;
        const resolvedDateKey =
            selectedDateKey ||
            (drawerActiveSelectedPoint?.datum
                ? dayjs(
                      accessors.xAccessor(drawerActiveSelectedPoint.datum),
                  ).format('YYYY-MM-DD')
                : null);
        if (!resolvedDateKey) return null;
        const metric =
            sourceMetricsByDate[drawerActiveSourceId]?.[resolvedDateKey];
        if (!metric) return null;
        return {
            value: metric.value ?? null,
            change: metric.increase ?? null,
            label: SOURCE_CURRENT_VOLUME_LABEL_BY_ID[drawerActiveSourceId],
        };
    }, [
        drawerActiveSourceId,
        selectedDateKey,
        drawerActiveSelectedPoint,
        accessors,
        sourceMetricsByDate,
    ]);

    const onPointerMove = (e) => {
        const point = localPoint(e);
        if (!point) return;
        const nearestData = getNearestStackDatum(point.x);
        if (nearestData)
            showTooltip({
                tooltipLeft: x({ data: nearestData }),
                tooltipTop: yRange0 / 2,
                tooltipData: nearestData,
            });
    };

    const onChartClick = useCallback(
        (e) => {
            if (!hasDriversOfGrowthContext) return;
            const point = localPoint(e);
            if (!point) return;
            const nearestData = getNearestStackDatum(point.x);
            if (!(nearestData && !nearestData.overview)) return;

            const dateKey = dayjs(nearestData.date).format('YYYY-MM-DD');
            const dominantPoint = getDominantDriversPointByDate(
                nearestData.date,
            );
            const fallbackSourceId = availableSourceIds[0];
            const sourceId = dominantPoint?.sourceId || fallbackSourceId;
            if (!sourceId) return;

            const datum = sourceRowsByDate[sourceId]?.[dateKey];
            if (!datum) return;

            setSelectedDateKey(dateKey);
            setSelectedDriversSourceId(sourceId);
            setSelectedPointData({ datum });
            setDrawerOpen(true);
        },
        [
            hasDriversOfGrowthContext,
            getNearestStackDatum,
            getDominantDriversPointByDate,
            availableSourceIds,
            sourceRowsByDate,
        ],
    );

    const handleDrawerPointChange = useCallback(
        (nextPointData) => {
            setSelectedPointData(nextPointData);
            if (!nextPointData?.datum) return;
            setSelectedDateKey(
                dayjs(accessors.xAccessor(nextPointData.datum)).format(
                    'YYYY-MM-DD',
                ),
            );
        },
        [accessors],
    );

    const handleDrawerSourceChange = useCallback(
        (sourceId) => {
            setSelectedDriversSourceId(sourceId);
            const datumForDate = selectedDateKey
                ? sourceRowsByDate[sourceId]?.[selectedDateKey]
                : null;
            const fallbackDatum =
                sourceSeriesById[sourceId]?.historical?.[
                    sourceSeriesById[sourceId]?.historical?.length - 1
                ];
            const nextDatum = datumForDate || fallbackDatum;
            if (nextDatum) {
                setSelectedPointData({ datum: nextDatum });
                setSelectedDateKey(
                    dayjs(accessors.xAccessor(nextDatum)).format('YYYY-MM-DD'),
                );
            }
        },
        [selectedDateKey, sourceRowsByDate, sourceSeriesById, accessors],
    );

    const drawerTabs = useMemo(
        () => (
            <div className="flex items-center gap-1 rounded-xl bg-neutral-100 p-1">
                {availableSourceIds.map((sourceId) => (
                    <button
                        key={sourceId}
                        type="button"
                        className={`px-3 py-1.5 text-sm rounded-lg ${
                            selectedDriversSourceId === sourceId
                                ? 'bg-white shadow-sm text-primary'
                                : 'text-foreground hover:bg-neutral-200'
                        }`}
                        onClick={() => handleDrawerSourceChange(sourceId)}
                    >
                        {SOURCE_LABEL_BY_ID[sourceId]}
                    </button>
                ))}
            </div>
        ),
        [availableSourceIds, handleDrawerSourceChange, selectedDriversSourceId],
    );

    const tooltipInstagramEstimated = !!tooltipData?.instagram_backfill;
    const tooltipTiktokEstimated = !!tooltipData?.tiktok_backfill;

    const tooltipEstimatedSourceLabels = useMemo(
        () =>
            tooltipData && !tooltipData.overview
                ? SOURCE_PRIORITY.filter(
                      (sourceId) =>
                          availableSourceIds.includes(sourceId) &&
                          !!tooltipData?.[`${sourceId}_backfill`],
                  ).map((sourceId) => SOURCE_LABEL_BY_ID[sourceId])
                : [],
        [tooltipData, availableSourceIds],
    );
    const activeBackfillIdx = useMemo(
        () =>
            data.find((series) => series.id === selectedDriversSourceId)
                ?.timeSeries?.[0]?.backfill,
        [data, selectedDriversSourceId],
    );
    const isDatumEstimated = useCallback(
        (datum) => activeBackfillIdx != null && !!datum?.[activeBackfillIdx],
        [activeBackfillIdx],
    );

    return (
        <div css="position: relative;">
            <svg width={width} height={height}>
                <AxisLeft
                    left={Y_AXIS_WIDTH}
                    scale={yScale}
                    orientation="left"
                    tickFormat={yAxisTickFormat}
                    tickComponent={TickLabel}
                    numTicks={3}
                    hideZero={true}
                    hideAxisLine={true}
                    hideTicks={true}
                />
                <AxisBottom
                    orientation="bottom"
                    top={yRange0}
                    scale={xScale}
                    tickValues={xAxisTickValues.range(xMin, xMax, 1)}
                    tickFormat={xAxisTickFormat}
                    tickComponent={TickLabel}
                    hideAxisLine={true}
                    hideTicks={true}
                />
                <DataSourceAreaStacks
                    keys={predictionKeys}
                    stackData={predictionStackData}
                    x={x}
                    y0={y0}
                    y1={y1}
                    strokeWidth={STROKE_WIDTH}
                />
                <DataSourceAreaStacks
                    keys={keys}
                    stackData={stackData}
                    x={x}
                    y0={y0}
                    y1={y1}
                    strokeWidth={STROKE_WIDTH}
                />
                {tooltipOpen ? (
                    <PopularityTimeSeriesTooltipSVG
                        tooltipData={tooltipData}
                        tooltipLeft={tooltipLeft}
                        keys={keys}
                        stackData={stackData}
                        predictionKeys={predictionKeys}
                        predictionStackData={predictionStackData}
                        x={x}
                        y0={y0}
                        y1={y1}
                        yRange0={yRange0}
                        yRange1={yRange1}
                    />
                ) : null}
                <rect
                    x={0}
                    y={0}
                    width={width ?? 0}
                    height={yRange0 ?? 0}
                    fill="transparent"
                    style={{
                        cursor: hasDriversOfGrowthContext
                            ? 'pointer'
                            : 'default',
                    }}
                    onPointerMove={onPointerMove}
                    onPointerOut={() => hideTooltip()}
                    onPointerUp={() => hideTooltip()}
                    onClick={
                        hasDriversOfGrowthContext ? onChartClick : undefined
                    }
                />
            </svg>
            {hasDriversOfGrowthContext &&
            tooltipOpen &&
            !tooltipData?.overview &&
            tooltipDriversPointData &&
            tooltipDominantSource ? (
                <DriversOfGrowthTooltip
                    left={tooltipLeft}
                    top={tooltipTop}
                    chartWidth={width}
                    chartHeight={height}
                    accessors={tooltipDriversAccessors}
                    tooltipData={{
                        datum: tooltipDriversPointData,
                        name,
                    }}
                    tooltipOpen={tooltipOpen}
                    formatAndParseTime={formatAndParseTime}
                    metricLabel="popularity"
                    sourceName="Popularity"
                    dataSource={tooltipDominantSource}
                    country={country}
                    category={category}
                    brand={brand}
                    dominantSourceImpact={tooltipDominantSourceImpact}
                    estimatedSources={tooltipEstimatedSourceLabels}
                />
            ) : null}
            {hasDriversOfGrowthContext &&
            tooltipOpen &&
            tooltipData?.overview &&
            predictionTooltipPointData ? (
                <DriversOfGrowthTooltip
                    left={tooltipLeft}
                    top={tooltipTop}
                    chartWidth={width}
                    chartHeight={height}
                    accessors={tooltipDriversAccessors}
                    tooltipData={{
                        datum: predictionTooltipPointData,
                        name,
                    }}
                    tooltipOpen={tooltipOpen}
                    formatAndParseTime={formatAndParseTime}
                    metricLabel="popularity"
                    sourceName="Popularity Forecast"
                    dataSource={tooltipPredictionSource}
                    country={country}
                    category={category}
                    brand={brand}
                />
            ) : null}
            {!hasDriversOfGrowthContext && tooltipOpen ? (
                <TooltipCard
                    left={tooltipLeft}
                    top={height / 2 - 90}
                    chartWidth={width}
                    chartHeight={height}
                    offset={40}
                    width={232}
                    height={212}
                >
                    <Stack $space="1rem">
                        <div>
                            <TooltipDate>
                                {tooltipData?.date
                                    ? formatAndParseTime(tooltipData?.date)
                                    : null}
                            </TooltipDate>
                            <TooltipName>{name}</TooltipName>
                        </div>
                        {tooltipData?.overview ? (
                            <TooltipMetricLabelWrapper $dataSource="prediction">
                                <TooltipMetric>
                                    {formatVolume(tooltipData?.overview)}
                                </TooltipMetric>
                                <TooltipMetricLabel>
                                    Predicted Pop.
                                </TooltipMetricLabel>
                            </TooltipMetricLabelWrapper>
                        ) : (
                            <>
                                <TooltipMetricLabelWrapper $dataSource="google">
                                    <TooltipMetric>
                                        {formatVolume(tooltipData?.google)}
                                    </TooltipMetric>
                                    <TooltipMetricLabel>
                                        Google Pop.
                                    </TooltipMetricLabel>
                                </TooltipMetricLabelWrapper>
                                {tooltipData?.instagram !== undefined && (
                                    <TooltipMetricLabelWrapper $dataSource="instagram">
                                        <TooltipMetric
                                            $estimated={
                                                tooltipInstagramEstimated
                                            }
                                        >
                                            {formatVolume(
                                                tooltipData?.instagram,
                                            )}
                                        </TooltipMetric>
                                        <TooltipMetricLabel
                                            $estimated={
                                                tooltipInstagramEstimated
                                            }
                                        >
                                            Instagram Pop.
                                        </TooltipMetricLabel>
                                        {tooltipInstagramEstimated && (
                                            <TooltipEstimateNote>
                                                * Instagram estimated
                                            </TooltipEstimateNote>
                                        )}
                                    </TooltipMetricLabelWrapper>
                                )}
                                <TooltipMetricLabelWrapper $dataSource="tiktok">
                                    <TooltipMetric
                                        $estimated={tooltipTiktokEstimated}
                                    >
                                        {formatVolume(tooltipData?.tiktok)}
                                    </TooltipMetric>
                                    <TooltipMetricLabel
                                        $estimated={tooltipTiktokEstimated}
                                    >
                                        TikTok Pop.
                                    </TooltipMetricLabel>
                                    {tooltipTiktokEstimated && (
                                        <TooltipEstimateNote>
                                            * TikTok estimated
                                        </TooltipEstimateNote>
                                    )}
                                </TooltipMetricLabelWrapper>
                            </>
                        )}
                    </Stack>
                </TooltipCard>
            ) : null}
            {hasDriversOfGrowthContext && drawerActiveSource && (
                <Drawer
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    $width="42rem"
                >
                    <DriversOfGrowthDrawer
                        selectedPointData={drawerActiveSelectedPoint}
                        setSelectedPointData={handleDrawerPointChange}
                        accessors={accessors}
                        country={country}
                        category={category}
                        categoryDepth={categoryDepth}
                        brand={brand}
                        dataSource={drawerActiveSource}
                        historicalData={drawerActiveHistoricalData}
                        navigationMiddleContent={drawerTabs}
                        headerPrimaryMetric={drawerHeaderPrimaryMetric}
                        isDatumEstimated={isDatumEstimated}
                    />
                </Drawer>
            )}
        </div>
    );
}

function MultiTimeSeriesChartOverview({
    children,
    data,
    chartHeight,
    isLoading,
    showPlaceholder,
    placeholderMessage,
    Wrapper = TikTokTimeSeriesWrapper,
    showKey = true,
    ...props
}) {
    return (
        <Wrapper>
            {children}
            {showPlaceholder ? (
                <Placeholder
                    height={chartHeight + (showKey ? 40 : 0)}
                    $showBackground={!showKey}
                    isLoading={isLoading}
                >
                    {placeholderMessage}
                </Placeholder>
            ) : (
                <div>
                    {showKey ? (
                        <TimeSeriesKey
                            data={data}
                            css={`
                                padding: 1rem 1rem 0;
                                font-size: 1rem;
                                font-weight: 600;
                                margin-bottom: -0.75em;
                                min-height: 2.5rem;
                                color: ${({ theme }) => theme.gray_2};
                            `}
                        />
                    ) : null}
                    <Chart as="div" height={chartHeight}>
                        <ParentSize>
                            {({ width, height }) => (
                                <ErrorBoundary
                                    FallbackComponent={() => (
                                        <Placeholder
                                            $showBackground={true}
                                            height={chartHeight}
                                        >
                                            There was a problem showing this
                                            time series.
                                        </Placeholder>
                                    )}
                                >
                                    <MultiTimeSeriesChart
                                        data={data.filter(
                                            ({ timeSeries }) =>
                                                timeSeries &&
                                                timeSeries.length > 0,
                                        )}
                                        width={width}
                                        height={height}
                                        {...props}
                                    />
                                </ErrorBoundary>
                            )}
                        </ParentSize>
                    </Chart>
                </div>
            )}
        </Wrapper>
    );
}

export default MultiTimeSeriesChartOverview;
